/*
 * -----------------------------------------------------------------------
 *  <copyright file="risk-assessment.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import gql from 'graphql-tag';
import {
    LibraryControlFragment,
    LibraryRiskFragment,
    RiskEvaluationModelFragment,
    StudyControlFragment,
    CompleteRiskFragment,
    RiskObservationFragment, LibraryFunctionalDataReviewsFragment,
    LibraryRisksLovUsageFragment,
    LibraryControlsLovUsageFragment,
    LibraryFunctionalDataReviewsLovUsageFragment
} from './fragments';

// Queries in this file are used for legacy risk-assessment-related areas,
// like the admin pages (e.g., risk library), or the legacy edit multiple fields modal

export const GET_COMPLETE_RISK = gql`
    query getCompleteRisk($id: ObjectId!) {
        risk(id: $id) {
            ...CompleteRiskFields
        }
    }
    ${CompleteRiskFragment}
`;

export const GET_COMPLETE_RISKS = gql`
    query getCompleteRisks($studyId: ObjectId!) {
        risks(studyId: $studyId) {
            ...CompleteRiskFields
        }
    }
    ${CompleteRiskFragment}
`;

export const GET_STUDY_CONTROLS = gql`
    query getStudyControls($studyId: ObjectId!) {
        studyControls(studyId: $studyId) {
            ...StudyControlFields
        }
    }
    ${StudyControlFragment}
`;

export const GET_RISK_EVALUATION_MODELS = gql`
    query getRiskEvaluationModels {
        riskEvaluationModels {
            ...RiskEvaluationModelFields
        }
    }
    ${RiskEvaluationModelFragment}
`;

export const SAVE_RISK_EVALUATION_MODEL = gql`
    mutation saveRiskEvaluationModel($model: RiskEvaluationModelInput!, $reason: String) {
        saveRiskEvaluationModel(model: $model, reason: $reason) {
            ...RiskEvaluationModelFields
        }
    }
    ${RiskEvaluationModelFragment}
`;

export const GET_LIBRARY_RISKS = gql`
    query getLibraryRisks($organisationId: ObjectId, $includeSystemRisks: Boolean) {
        libraryRisks(organisationId: $organisationId, includeSystemRisks: $includeSystemRisks) {
            ...LibraryRiskFields
        }
    }
    ${LibraryRiskFragment}
`;

export const UPDATE_LIBRARY_RISK = gql`
    mutation updateLibraryRisk($risk: LibraryRiskInput!, $reason: String) {
        updateLibraryRisk(risk: $risk, reason: $reason) {
            ...LibraryRiskFields
        }
    }
    ${LibraryRiskFragment}
`;

export const UPDATE_LIBRARY_RISKS = gql`
    mutation updateLibraryRisks($risks: [LibraryRiskInput!]!, $reason: String) {
        updateLibraryRisks(risks: $risks, reason: $reason) {
            ...LibraryRiskFields
        }
    }
    ${LibraryRiskFragment}
`;

export const UPDATE_LIBRARY_RISKS_DISPLAY_ORDER = gql`
    mutation updateLibraryRisksDisplayOrder($libraryRisksDisplayOrder: [LibraryRiskDisplayOrderInputType]) {
        updateLibraryRisksDisplayOrder(libraryRisksDisplayOrder: $libraryRisksDisplayOrder)
    }
`;

export const DELETE_LIBRARY_RISK = gql`
    mutation deleteLibraryRisk($id: ObjectId!, $reason: String) {
        deleteLibraryRisk(id: $id, reason: $reason)
    }
`;

export const GET_LIBRARY_CONTROLS = gql`
    query getLibraryControls($organisationId: ObjectId, $includeSystemControls: Boolean) {
        libraryControls(organisationId: $organisationId, includeSystemControls: $includeSystemControls) {
            ...LibraryControlFields
        }
    }
    ${LibraryControlFragment}
`;

export const ORGANISATION_LIBRARY_RISKS_LOV_USAGE = gql`
    query organisationLibraryRisksLovUsage($organisationId: ObjectId!) {
        libraryRisks(organisationId: $organisationId, includeSystemRisks: true) {
            ...LibraryRisksLovUsageFields
        }
    }
    ${LibraryRisksLovUsageFragment}
`;

export const ORGANISATION_LIBRARY_CONTROLS_LOV_USAGE = gql`
    query organisationLibraryControlsLovUsage($organisationId: ObjectId!) {
        libraryControls(organisationId: $organisationId, includeSystemControls: true) {
            ...LibraryControlsLovUsageFields
        }
    }
    ${LibraryControlsLovUsageFragment}
`;

export const ORGANISATION_LIBRARY_FUNCTIONAL_DATA_REVIEWS_LOV_USAGE = gql`
    query organisationLibraryFunctionalDataReviewsLovUsage {
        libraryFunctionalDataReviews {
            ...LibraryFunctionalDataReviewsLovUsageFields
        }
    }
    ${LibraryFunctionalDataReviewsLovUsageFragment}
`;

export const UPDATE_LIBRARY_CONTROL = gql`
    mutation updateLibraryControl($control: LibraryControlInput!, $reason: String) {
        updateLibraryControl(control: $control, reason: $reason) {
            ...LibraryControlFields
        }
    }
    ${LibraryControlFragment}
`;

export const UPDATE_LIBRARY_CONTROLS = gql`
    mutation updateLibraryControls($controls: [LibraryControlInput!]!, $reason: String) {
        updateLibraryControls(controls: $controls, reason: $reason) {
            ...LibraryControlFields
        }
    }
    ${LibraryControlFragment}
`;

export const DELETE_LIBRARY_CONTROL = gql`
    mutation deleteLibraryControl($id: ObjectId!, $reason: String) {
        deleteLibraryControl(id: $id, reason: $reason)
    }
`;

export const GET_RISK_OBSERVATIONS_AND_ACTIONS = gql`
    query riskObservations($studyId: ObjectId!) {
        riskObservations(studyId: $studyId) {
            ...RiskObservationFields
        }
    }
    ${RiskObservationFragment}
`;

export const GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_COMPLETE_RISKS = gql`
    query riskObservationsRisks($studyId: ObjectId!) {
        risks(studyId: $studyId) {
            ...CompleteRiskFields
        },
        riskObservations(studyId: $studyId) {
            ...RiskObservationFields
        }
    }
    ${CompleteRiskFragment}
    ${RiskObservationFragment}
`;

export const GET_ORGANISATION_RISKS = gql`
    query organisationRisks {
        organisationRisks {
            ...CompleteRiskFields
        },
    }
    ${CompleteRiskFragment}
`;

export const DELETE_RISK_OBSERVATION = gql`
    mutation deleteRiskObservation($id: ObjectId!, $reason: String) {
        deleteRiskObservation(id: $id, reason: $reason)
    }
`;

export const RESTORE_RISK_OBSERVATION = gql`
    mutation unDeleteRiskObservation($id: ObjectId!, $unDeleteActions: Boolean = false) {
        unDeleteRiskObservation(id: $id, unDeleteActions: $unDeleteActions)
    }
`;

export const DELETE_RISK_OBSERVATION_ACTION = gql`
    mutation deleteRiskObservationAction($actionId: ObjectId!, $observationId: ObjectId!, $reason: String) {
        deleteRiskObservationAction(actionId: $actionId, observationId: $observationId, reason: $reason)
    }
`;

export const RESTORE_RISK_OBSERVATION_ACTION = gql`
    mutation unDeleteRiskObservationAction($actionId: ObjectId!, $observationId: ObjectId!) {
        unDeleteRiskObservationAction(actionId: $actionId, observationId: $observationId)
    }
`;

export const SEND_RISK_MONITORING_EMAIL = gql`
    mutation sendRiskMonitoringEmail($cc: String, $notes: String, $observationIds: [ObjectId]!, $actionIds: [ObjectId]!) {
        sendRiskMonitoringEmail(cc: $cc, notes: $notes, observationIds: $observationIds, actionIds: $actionIds)
    }    
`;

export const GET_LIBRARY_FUNCTIONAL_DATA_REVIEWS = gql`
    query libraryFunctionalDataReviews {
        libraryFunctionalDataReviews {
            ...LibraryFunctionalDataReviewFields
        }
    }
${LibraryFunctionalDataReviewsFragment}
`;

export const UPSERT_LIBRARY_FUNCTIONAL_DATA_REVIEWS = gql`
    mutation upsertLibraryFunctionalDataReview($libraryFunctionalDataReview: LibraryFunctionalDataReviewInput!, $reason: String) {
        upsertLibraryFunctionalDataReview(libraryFunctionalDataReview : $libraryFunctionalDataReview, reason: $reason) {
            ...LibraryFunctionalDataReviewFields
        }
    }
    ${LibraryFunctionalDataReviewsFragment}`;
